<script>
    import {getStorePacks} from '../stores.js';

    const COOLDOWN_SECONDS = 30;

    let retrying = $state(false);
    let cooldown = $state(0);
    let cooldownInterval = null;

    let disabled = $derived(retrying || cooldown > 0);

    async function handleRetry() {
        if (disabled) return;
        retrying = true;
        try {
            // Not force: the backend skips its webshopRedirectRequirement gate (castle level +
            // customer) on a forced load, so a forced retry showed non-customers the full store
            // until the next reload. The failed load has already cleared the in-flight dedupe.
            await getStorePacks();
        } finally {
            retrying = false;
            cooldown = COOLDOWN_SECONDS;
            cooldownInterval = setInterval(() => {
                cooldown--;
                if (cooldown <= 0) {
                    clearInterval(cooldownInterval);
                    cooldownInterval = null;
                }
            }, 1000);
        }
    }
</script>

<div class="store-error">
    <div class="store-error__card">
        <div class="store-error__icon">
            <img src="/images/activityChests/activity-chest1.png" alt="" class="store-error__chest" />
        </div>

        <h2 class="store-error__title">We couldn't load your supplies right now</h2>
        <p class="store-error__subtitle">Something went wrong on our end. Your progress is safe — give it another try.</p>

        <button class="store-error__button" onclick={handleRetry} disabled={disabled}>
            {#if retrying}
                <span class="store-error__spinner"></span>
                Loading...
            {:else if cooldown > 0}
                Try Again ({cooldown}s)
            {:else}
                <svg viewBox="0 0 20 20" fill="currentColor" class="store-error__retry-icon">
                    <path d="M4.5 2.5C4.5 2.22 4.72 2 5 2s.5.22.5.5v4h4c.28 0 .5.22.5.5s-.22.5-.5.5H5a.5.5 0 01-.5-.5v-4.5z"/>
                    <path d="M3.13 10.13a7 7 0 0112.57-3.07l.7-.7A8 8 0 002.1 10.5l1.03-.37z"/>
                    <path d="M15.5 17.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-4h-4a.5.5 0 010-1H15c.28 0 .5.22.5.5v4.5z"/>
                    <path d="M16.87 9.87a7 7 0 01-12.57 3.07l-.7.7A8 8 0 0017.9 9.5l-1.03.37z"/>
                </svg>
                Try Again
            {/if}
        </button>
    </div>
</div>

<style lang="scss">
    @use '../styles/vars.scss' as *;

    .store-error {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        padding: 40px 20px;
        margin-top: 50px;

        @media (max-width: 700px) {
            margin-top: 35px;
            min-height: 300px;
            padding: 30px 16px;
        }
    }

    .store-error__card {
        text-align: center;
        max-width: 420px;
        padding: 48px 40px;
        border-radius: $store-pack-border-radius;
        border: 1px solid rgba(255, 255, 255, 0.08);
        background:
            radial-gradient(ellipse at 50% 30%, var(--color-lightGrey), transparent 65%),
            var(--color-darkGrey);
        background-blend-mode: soft-light, normal;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
        position: relative;
        overflow: hidden;

        // Slow diagonal shimmer sweep
        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 60%;
            height: 100%;
            background: linear-gradient(
                105deg,
                transparent 30%,
                rgba(255, 255, 255, 0.04) 45%,
                rgba(255, 255, 255, 0.08) 50%,
                rgba(255, 255, 255, 0.04) 55%,
                transparent 70%
            );
            animation: shimmer 6s ease-in-out infinite;
            pointer-events: none;
        }

        @media (max-width: 700px) {
            padding: 36px 24px;
        }
    }

    @keyframes shimmer {
        0%, 100% { left: -100%; }
        50% { left: 140%; }
    }

    .store-error__icon {
        position: relative;
        width: 140px;
        height: 140px;
        margin: 0 auto 20px;
    }

    .store-error__chest {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 120px;
        height: auto;
        filter:
            drop-shadow(0 0 20px var(--color-lightGrey))
            drop-shadow(0 4px 8px rgba(0, 0, 0, 0.6));
    }

    .store-error__title {
        position: relative;
        font-family: var(--font-family-sans-condensed);
        font-weight: 700;
        font-size: 1.4rem;
        color: var(--font-color);
        margin: 0 0 12px;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);

        @media (max-width: 700px) {
            font-size: 1.2rem;
        }
    }

    .store-error__subtitle {
        position: relative;
        font-family: var(--font-family-sans);
        font-size: 0.9rem;
        color: rgba(255, 255, 255, 0.5);
        line-height: 1.5;
        margin: 0 0 28px;
    }

    .store-error__button {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 32px;
        font-family: var(--font-family-sans-condensed);
        font-weight: 700;
        font-size: 1rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--font-color);
        border: 2px solid var(--color-lightGrey);
        border-radius: 6px;
        cursor: pointer;
        background: var(--color-darkGrey);
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background: var(--color-lightGrey);
            color: var(--color-darkGrey);
            box-shadow:
                0 4px 16px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
            transform: translateY(-1px);
        }

        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .store-error__retry-icon {
        width: 18px;
        height: 18px;
    }

    .store-error__spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: var(--color-lightGrey);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
</style>
